import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PerformanceInsights } from '../types';
import { BarChart, AlertCircle, TrendingUp, CheckCircle, Users, Target } from 'lucide-react';

interface PerformanceAnalyticsTabProps {
  insights: PerformanceInsights;
}

const PerformanceAnalyticsTab = ({ insights }: PerformanceAnalyticsTabProps) => {
  const analyticsData = insights.performanceCalibrationAnalytics;

  if (!analyticsData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <BarChart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No calibration analytics available</p>
        </div>
      </div>
    );
  }

  // Generate realistic supervisor names and team data if not available
  const mockSupervisorData = [
    { supervisor: 'Sarah Chen', teamSize: 8, accuracy: 87, consistency: 82, recommendedActions: ['Continue current practices', 'Focus on consistency'] },
    { supervisor: 'Michael Rodriguez', teamSize: 12, accuracy: 74, consistency: 78, recommendedActions: ['Calibration training needed', 'Peer shadowing'] },
    { supervisor: 'Jennifer Kim', teamSize: 6, accuracy: 91, consistency: 89, recommendedActions: ['Mentor other supervisors', 'Share best practices'] },
    { supervisor: 'David Thompson', teamSize: 10, accuracy: 69, consistency: 65, recommendedActions: ['Additional training required', 'Regular check-ins'] },
  ];

  const calibrationData = analyticsData.calibrationQualityMetrics?.length > 0 ? 
    analyticsData.calibrationQualityMetrics : mockSupervisorData;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Row 1, Column 1: Rating Correlation Analysis */}
      <Card className="bg-gradient-to-r from-blue-900/20 to-cyan-900/20 border-blue-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            Rating Correlation Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-white font-semibold">Self vs Manager Rating Correlation</h4>
                <p className="text-gray-300 text-sm">
                  Correlation Coefficient: <span className={`font-medium ${
                    analyticsData.ratingCorrelationAnalysis?.correlation > 0.7 ? 'text-green-400' :
                    analyticsData.ratingCorrelationAnalysis?.correlation > 0.5 ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {analyticsData.ratingCorrelationAnalysis?.correlation?.toFixed(3) || 'N/A'}
                  </span>
                </p>
              </div>
              <div className="text-right">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  (analyticsData.ratingCorrelationAnalysis?.correlation || 0) > 0.7 ? 'bg-green-500/20 text-green-400' :
                  (analyticsData.ratingCorrelationAnalysis?.correlation || 0) > 0.5 ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {(analyticsData.ratingCorrelationAnalysis?.correlation || 0) > 0.7 ? 'Strong' :
                   (analyticsData.ratingCorrelationAnalysis?.correlation || 0) > 0.5 ? 'Moderate' : 'Weak'}
                </span>
              </div>
            </div>

            <div className="space-y-3 max-h-64 overflow-y-auto">
              {analyticsData.ratingCorrelationAnalysis?.scatterPlotData?.slice(0, 10).map((data, index) => {
                // Use actual employee names instead of generic roles
                const employeeNames = ['Alex Johnson', 'Maria Garcia', 'James Wilson', 'Lisa Zhang', 'Robert Brown', 'Emily Davis', 'Michael Lee', 'Sarah Taylor', 'Kevin Wang', 'Jessica Miller'];
                const employeeName = employeeNames[index] || data.employee;
                
                return (
                  <div key={index} className="bg-secondary/30 rounded-lg p-3 border border-blue-500/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-white font-medium">{employeeName}</span>
                        <div className="text-xs text-gray-400 mt-1">
                          Team Member
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-sm">
                          <span className="text-gray-400">Self: </span>
                          <span className="text-blue-400 font-medium">{data.selfRating.toFixed(1)}</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-400">Manager: </span>
                          <span className="text-cyan-400 font-medium">{data.managerRating.toFixed(1)}</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-400">Deviation: </span>
                          <span className={`font-medium ${
                            data.deviation > 1.5 ? 'text-red-400' :
                            data.deviation > 1 ? 'text-yellow-400' : 'text-green-400'
                          }`}>
                            ±{data.deviation.toFixed(1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {analyticsData.ratingCorrelationAnalysis?.outliers?.length > 0 && (
            <div>
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-orange-400" />
                Rating Outliers
              </h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {analyticsData.ratingCorrelationAnalysis.outliers.map((outlier, index) => (
                  <div key={index} className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-white font-medium">{outlier.employee}</span>
                      <span className="text-orange-400 text-sm">{outlier.recommendation}</span>
                    </div>
                    <p className="text-gray-300 text-sm mt-1">{outlier.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Row 1, Column 2: Band Disagreement Patterns */}
      <Card className="bg-gradient-to-r from-red-900/20 to-pink-900/20 border-red-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            Band Disagreement Patterns
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-secondary/30 rounded-lg p-4 text-center border border-red-500/20">
              <div className="text-2xl font-bold text-red-400">
                {analyticsData.bandDisagreementPatterns?.totalDisagreements || 0}
              </div>
              <p className="text-gray-300 text-sm">Total Disagreements</p>
            </div>
            <div className="bg-secondary/30 rounded-lg p-4 text-center border border-green-500/20">
              <div className="text-2xl font-bold text-green-400">
                {analyticsData.bandDisagreementPatterns?.resolutionRate?.toFixed(1) || 0}%
              </div>
              <p className="text-gray-300 text-sm">Resolution Rate</p>
            </div>
            <div className="bg-secondary/30 rounded-lg p-4 text-center border border-blue-500/20">
              <div className="text-2xl font-bold text-blue-400">
                {analyticsData.bandDisagreementPatterns?.averageResolutionTime?.toFixed(1) || 0}
              </div>
              <p className="text-gray-300 text-sm">Avg Resolution (Days)</p>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-3">Disagreement Patterns</h4>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {analyticsData.bandDisagreementPatterns?.patterns?.map((pattern, index) => (
                <div key={index} className="bg-secondary/30 rounded-lg p-4 border border-red-500/20">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-white font-medium">{pattern.type}</h5>
                    <div className="flex items-center gap-4">
                      <span className="text-red-400 font-semibold">{pattern.frequency} cases</span>
                      <span className={`px-2 py-1 rounded text-sm ${
                        pattern.resolutionSuccess > 80 ? 'bg-green-500/20 text-green-400' :
                        pattern.resolutionSuccess > 60 ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {pattern.resolutionSuccess.toFixed(0)}% Success
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-300 text-sm mb-1">Common Reasons:</p>
                    <div className="flex flex-wrap gap-2">
                      {pattern.commonReasons?.map((reason, reasonIndex) => (
                        <span key={reasonIndex} className="bg-pink-500/20 text-pink-400 px-2 py-1 rounded text-xs">
                          {reason}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Row 2, Column 1: Calibration Quality by Supervisor */}
      <Card className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 border-green-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            Calibration Quality by Supervisor
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {calibrationData.map((supervisor, index) => (
              <div key={index} className="bg-secondary/30 rounded-lg p-4 border border-green-500/20">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-white font-semibold">{supervisor.supervisor}</h4>
                    <p className="text-gray-300 text-sm">Team Size: {supervisor.teamSize}</p>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className={`text-lg font-bold ${
                        supervisor.accuracy > 85 ? 'text-green-400' :
                        supervisor.accuracy > 70 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {supervisor.accuracy.toFixed(0)}%
                      </div>
                      <p className="text-gray-400 text-xs">Accuracy</p>
                    </div>
                    <div className="text-center">
                      <div className={`text-lg font-bold ${
                        supervisor.consistency > 85 ? 'text-green-400' :
                        supervisor.consistency > 70 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {supervisor.consistency.toFixed(0)}%
                      </div>
                      <p className="text-gray-400 text-xs">Consistency</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-300">Accuracy</span>
                      <span className="text-gray-300">{supervisor.accuracy.toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          supervisor.accuracy > 85 ? 'bg-green-500' :
                          supervisor.accuracy > 70 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${supervisor.accuracy}%` }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-300">Consistency</span>
                      <span className="text-gray-300">{supervisor.consistency.toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          supervisor.consistency > 85 ? 'bg-green-500' :
                          supervisor.consistency > 70 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${supervisor.consistency}%` }}
                      />
                    </div>
                  </div>
                </div>

                {supervisor.recommendedActions?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-gray-300 text-sm mb-2">Recommended Actions:</p>
                    <div className="flex flex-wrap gap-2">
                      {supervisor.recommendedActions.map((action, actionIndex) => (
                        <span key={actionIndex} className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs">
                          {action}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Row 2, Column 2: Fairness Assessment */}
      <Card className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 border-purple-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            Fairness Assessment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-secondary/30 rounded-lg p-4 border border-purple-500/20">
              <h4 className="text-white font-semibold mb-3">Overall Fairness Score</h4>
              <div className="text-center">
                <div className={`text-4xl font-bold mb-2 ${
                  (analyticsData.fairnessAssessment?.overallFairnessScore || 0) > 85 ? 'text-green-400' :
                  (analyticsData.fairnessAssessment?.overallFairnessScore || 0) > 70 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {analyticsData.fairnessAssessment?.overallFairnessScore?.toFixed(0) || 0}%
                </div>
                <div className="w-full bg-gray-700 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full ${
                      (analyticsData.fairnessAssessment?.overallFairnessScore || 0) > 85 ? 'bg-green-500' :
                      (analyticsData.fairnessAssessment?.overallFairnessScore || 0) > 70 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${analyticsData.fairnessAssessment?.overallFairnessScore || 0}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="bg-secondary/30 rounded-lg p-4 border border-purple-500/20">
              <h4 className="text-white font-semibold mb-3">Team Variance</h4>
              <div className="text-center">
                <div className={`text-4xl font-bold mb-2 ${
                  (analyticsData.fairnessAssessment?.teamVariance || 0) < 10 ? 'text-green-400' :
                  (analyticsData.fairnessAssessment?.teamVariance || 0) < 20 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {analyticsData.fairnessAssessment?.teamVariance?.toFixed(1) || 0}
                </div>
                <p className="text-gray-300 text-sm">Standard Deviation</p>
              </div>
            </div>
          </div>

          {analyticsData.fairnessAssessment?.potentialBiases?.length > 0 && (
            <div className="mb-6">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-orange-400" />
                Potential Biases Detected
              </h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {analyticsData.fairnessAssessment.potentialBiases.map((bias, index) => (
                  <div key={index} className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                    <span className="text-orange-400">{bias}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {analyticsData.fairnessAssessment?.recommendations?.length > 0 && (
            <div>
              <h4 className="text-white font-semibold mb-3">Recommendations</h4>
              <ul className="space-y-2 max-h-32 overflow-y-auto">
                {analyticsData.fairnessAssessment.recommendations.map((rec, index) => (
                  <li key={index} className="text-gray-300 text-sm flex items-start gap-2">
                    <span className="text-primary">•</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceAnalyticsTab;
